import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './ExcepcionTurnoModal';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Excepciones de Turno";

const endpoint = ENDPOINTS.excepcionTurno;
const endpointById = ENDPOINTS.excepcionTurnoById;
const modeloExcepcion = Models.excepcionTurno;

// 👇 para armar el nombre del chofer desde Persona
const endpointChofer = ENDPOINTS.chofer;
const modeloChofer = Models.chofer;

const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const maps = {
  mapCreate: (f) => ({
    idChofer: Number(f.idChofer) || 0,
    nombreExcepcion: f.nombreExcepcion,
    descripcion: f.descripcion,
    fechaInicio: f.fechaInicio,
    horaInicio: f.horaInicio,
    horaFin: f.horaFin,
    fechaFin: f.fechaFin,
    motivo: f.motivo,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idExcepcion: id,
    idChofer: Number(f.idChofer) || 0,
    nombreExcepcion: f.nombreExcepcion,
    descripcion: f.descripcion,
    fechaInicio: f.fechaInicio,
    horaInicio: f.horaInicio,
    horaFin: f.horaFin,
    fechaFin: f.fechaFin,
    motivo: f.motivo,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  // 🔹 Estado para AlertDialog
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1) Cargar excepciones, choferes, empleados y personas
      const [resExc, resChof, resEmp, resPer] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointChofer),
        axios.get(endpointEmpleado),
        axios.get(endpointPersona),
      ]);

      let excepciones = (resExc.data || []).map(x => modeloExcepcion(x));
      let choferes = (resChof.data || []).map(x => modeloChofer(x));
      let empleados = (resEmp.data || []).map(x => modeloEmpleado(x));
      let personas = (resPer.data || []).map(x => modeloPersona(x));

      // 2) Mapas rápidos por id
      const empleadosById = new Map(
        empleados.map(e => [e.idEmpleado, e])
      );
      const personasById = new Map(
        personas.map(p => [p.idPersona, p])
      );

      // 3) Enriquecer chofer con nombre de persona
      const choferNameMap = {};
      choferes.forEach(ch => {
        const emp = ch.idEmpleado ? empleadosById.get(ch.idEmpleado) : null;
        const per = emp && emp.idPersona ? personasById.get(emp.idPersona) : null;

        const fullName = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
          : (ch.nombre || '');

        const displayName =
          fullName ||
          (ch.numeroLicencia
            ? `Chofer #${ch.idChofer} - ${ch.numeroLicencia}`
            : `Chofer #${ch.idChofer}`);

        choferNameMap[ch.idChofer] = displayName;
      });

      // 4) Agregar choferNombre a cada excepción
      excepciones = excepciones.map(ex => ({
        ...ex,
        choferNombre: choferNameMap[ex.idChofer] || `Chofer #${ex.idChofer || ''}`,
      }));

      setRows(excepciones);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const nombre = (r.nombreExcepcion || '').toLowerCase();
      const descripcion = (r.descripcion || '').toLowerCase();
      const motivo = (r.motivo || '').toLowerCase();
      const choferNombre = (r.choferNombre || '').toLowerCase();
      const choferIdText = String(r.idChofer || '').toLowerCase();

      return (
        nombre.includes(q) ||
        descripcion.includes(q) ||
        motivo.includes(q) ||
        choferNombre.includes(q) ||   // 👈 buscar por nombre de persona
        choferIdText.includes(q)      // 👈 por si alguien busca por id
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idExcepcion === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);

      // 1. Obtener la excepción que se va a eliminar
      const excepcionAEliminar = rows.find(ex => ex.idExcepcion === deleteTarget.id);

      if (!excepcionAEliminar) {
        alert('No se encontró la excepción a eliminar.');
        setLoading(false);
        return;
      }

      // 2. Cargar todos los datos necesarios para validar reservas
      const [resReservas, resTurnos, resEmpleadosTurnos, resChoferes] = await Promise.all([
        axios.get(ENDPOINTS.reserva),
        axios.get(ENDPOINTS.turnos),
        axios.get(ENDPOINTS.empleadosTurnos),
        axios.get(endpointChofer),
      ]);

      const reservas = (resReservas.data || []).map(Models.reserva);
      const turnos = (resTurnos.data || []).map(Models.turnos);
      const empleadosTurnos = (resEmpleadosTurnos.data || []).map(Models.empleadosTurnos);
      const choferes = (resChoferes.data || []).map(modeloChofer);

      // 3. Importar la función helper dinámicamente
      const { getReservationsToCancel } = await import('../../../Utils/excepcionTurnoHelpers');

      // 4. Determinar qué reservas deben cancelarse
      const reservasACancelar = getReservationsToCancel(
        excepcionAEliminar,
        reservas,
        rows, // todas las excepciones actuales
        turnos,
        empleadosTurnos,
        choferes
      );

      // 5. Confirmar con el usuario si hay reservas afectadas
      if (reservasACancelar.length > 0) {
        const confirmMsg = `Al eliminar esta excepción de turno se cancelarán automáticamente ${reservasACancelar.length} reserva(s) que dependen exclusivamente de este horario extraordinario.\n\n¿Desea continuar?`;

        if (!window.confirm(confirmMsg)) {
          setLoading(false);
          setDeleteOpen(false);
          return;
        }

        // 6. Cancelar las reservas afectadas (estado 5 = CANCELLED)
        const cancelPromises = reservasACancelar.map(idReserva => {
          const reserva = reservas.find(r => (r.idReserva || r.IdReserva) === idReserva);
          if (!reserva) return Promise.resolve();

          // Construir payload similar a RHInbox.jsx
          const payload = {
            idReserva: idReserva,
            idSolicitante: reserva.idSolicitante || 0,
            idChofer: reserva.idChofer || 0,
            fechaInicio: reserva.fechaInicio,
            fechaFin: reserva.fechaFin,
            horaInicio: reserva.horaInicio,
            horaFin: reserva.horaFin,
            origenTexto: reserva.origenTexto || '',
            destinoTexto: reserva.destinoTexto || '',
            motivo: reserva.motivo || '',
            observaciones: (reserva.observaciones || '') +
              `\n[CANCELADA AUTOMÁTICAMENTE: Se eliminó la excepción de turno "${excepcionAEliminar.nombreExcepcion}" que cubría este horario]`,
            idEstadoReserva: 5, // CANCELLED
            fueraHorarioMin: !!reserva.fueraHorarioMin
          };

          // Solo incluir idTipoAprobacion si existe
          const idTipoAprobacion = reserva.idTipoAprobacion || reserva.IdTipoAprobacion;
          if (idTipoAprobacion) {
            payload.idTipoAprobacion = idTipoAprobacion;
          }

          return axios.put(ENDPOINTS.reserva, payload);
        });

        await Promise.all(cancelPromises);

        console.log(`✅ Se cancelaron ${reservasACancelar.length} reserva(s) afectadas por la eliminación de la excepción.`);
      }

      // 7. Eliminar la excepción de turno
      await axios.delete(endpointById(deleteTarget.id));

      setDeleteOpen(false);
      setEditingRowId(null);

      // 8. Mostrar mensaje de éxito
      if (reservasACancelar.length > 0) {
        alert(`Excepción eliminada exitosamente.\n${reservasACancelar.length} reserva(s) fueron canceladas automáticamente.`);
      }

      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar la excepción de turno: ' + (e.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);

      // 🔹 VALIDACIÓN 1: La excepción debe ser para mañana o después
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);

      const fechaExcepcion = new Date(payload.fechaInicio + 'T00:00:00');
      const manana = new Date(hoy);
      manana.setDate(hoy.getDate() + 1);

      if (fechaExcepcion < manana) {
        setAlertDialog({
          open: true,
          title: 'Anticipación Requerida',
          message: (
            <div>
              <p style={{ marginBottom: '0.75rem' }}>
                Las excepciones de turno deben crearse con <strong>al menos un día de anticipación</strong>.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                Para crear una excepción para hoy, debiste haberla registrado ayer antes de las 11:59 PM.
              </p>
              <p style={{ marginBottom: 0 }}>
                Por favor selecciona una fecha a partir de <strong>mañana</strong>.
              </p>
            </div>
          ),
          variant: 'warning'
        });
        setLoading(false);
        return;
      }

      // 🔹 VALIDACIÓN 2: No debe haber reservas para el día de la excepción
      // Cargar reservas del chofer
      const resReservas = await axios.get(ENDPOINTS.reserva);
      const reservas = (resReservas.data || []).map(Models.reserva);

      // Filtrar reservas del chofer para la fecha de la excepción
      const reservasEnFecha = reservas.filter(r => {
        const esDelChofer = Number(r.idChofer) === Number(payload.idChofer);
        if (!esDelChofer) return false;

        // Verificar si la reserva toca la fecha de la excepción
        const fechaInicioReserva = new Date(String(r.fechaInicio).slice(0, 10) + 'T00:00:00');
        const fechaFinReserva = new Date(String(r.fechaFin).slice(0, 10) + 'T00:00:00');

        // La reserva toca la fecha si:
        // - Inicia en la fecha de la excepción
        // - Termina en la fecha de la excepción
        // - Abarca la fecha de la excepción (inicia antes y termina después)
        return fechaInicioReserva <= fechaExcepcion && fechaFinReserva >= fechaExcepcion;
      });

      if (reservasEnFecha.length > 0) {
        // 🔹 Filtrar solo para el mensaje: excluir APROBADAS de la lista mostrada
        const reservasParaMostrar = reservasEnFecha.filter(r => Number(r.idEstadoReserva) !== 3);

        const estadosDetalle = reservasParaMostrar.map(r => {
          const estado = Number(r.idEstadoReserva);
          let estadoTexto = '';
          if (estado === 1) estadoTexto = 'PENDIENTE';
          else if (estado === 4) estadoTexto = 'RECHAZADA';
          else if (estado === 5) estadoTexto = 'CANCELADA';
          else estadoTexto = `Estado ${estado}`;

          return { estado: estadoTexto, motivo: r.motivo || 'Sin motivo' };
        });

        setAlertDialog({
          open: true,
          title: 'Reservas Existentes',
          message: (
            <div>
              <p style={{ marginBottom: '0.75rem' }}>
                No se puede crear/modificar la excepción de turno.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                Existen <strong>{reservasEnFecha.length} reserva(s)</strong> para el chofer seleccionado en la fecha <strong>{payload.fechaInicio}</strong>.
              </p>
              {reservasParaMostrar.length > 0 && (
                <ul style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem' }}>
                  {estadosDetalle.map((det, idx) => (
                    <li key={idx}>
                      <strong>{det.estado}</strong> - {det.motivo}
                    </li>
                  ))}
                </ul>
              )}
              <p style={{ marginBottom: 0 }}>
                Para poder crear una excepción de turno, primero debes <strong>cancelar o modificar</strong> todas las reservas existentes para ese día.
              </p>
            </div>
          ),
          variant: 'error'
        });
        setLoading(false);
        return;
      }

      if (mode === 'new') {
        // Modo nuevo: simplemente crear
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId =
          res?.data?.idExcepcion ??
          (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        // Modo edición: validar si afecta reservas existentes
        const excepcionAnterior = rows.find(ex => ex.idExcepcion === selectedId);

        if (!excepcionAnterior) {
          alert('No se encontró la excepción a actualizar.');
          setLoading(false);
          return;
        }

        // Cargar datos necesarios para validación
        const [resTurnos, resEmpleadosTurnos, resChoferes] = await Promise.all([
          axios.get(ENDPOINTS.turnos),
          axios.get(ENDPOINTS.empleadosTurnos),
          axios.get(endpointChofer),
        ]);

        const turnos = (resTurnos.data || []).map(Models.turnos);
        const empleadosTurnos = (resEmpleadosTurnos.data || []).map(Models.empleadosTurnos);
        const choferes = (resChoferes.data || []).map(modeloChofer);

        // Crear objeto con los nuevos datos
        const excepcionNueva = {
          ...excepcionAnterior,
          ...maps.mapUpdate(selectedId, payload)
        };

        // Importar función helper
        const { getReservationsAffectedByUpdate } = await import('../../../Utils/excepcionTurnoHelpers');

        // Determinar reservas afectadas
        const reservasACancelar = getReservationsAffectedByUpdate(
          excepcionAnterior,
          excepcionNueva,
          reservas,
          rows, // todas las excepciones actuales
          turnos,
          empleadosTurnos,
          choferes
        );

        // Confirmar si hay reservas afectadas
        if (reservasACancelar.length > 0) {
          const confirmMsg = `Al actualizar esta excepción de turno se cancelarán automáticamente ${reservasACancelar.length} reserva(s) que ya no podrán ser cubiertas por el nuevo horario.\n\n¿Desea continuar?`;

          if (!window.confirm(confirmMsg)) {
            setLoading(false);
            return;
          }

          // Cancelar las reservas afectadas
          const cancelPromises = reservasACancelar.map(idReserva => {
            const reserva = reservas.find(r => (r.idReserva || r.IdReserva) === idReserva);
            if (!reserva) return Promise.resolve();

            // Construir payload similar a RHInbox.jsx
            const payloadReserva = {
              idReserva: idReserva,
              idSolicitante: reserva.idSolicitante || 0,
              idChofer: reserva.idChofer || 0,
              fechaInicio: reserva.fechaInicio,
              fechaFin: reserva.fechaFin,
              horaInicio: reserva.horaInicio,
              horaFin: reserva.horaFin,
              origenTexto: reserva.origenTexto || '',
              destinoTexto: reserva.destinoTexto || '',
              motivo: reserva.motivo || '',
              observaciones: (reserva.observaciones || '') +
                `\n[CANCELADA AUTOMÁTICAMENTE: Se modificó la excepción de turno "${excepcionAnterior.nombreExcepcion}" y el nuevo horario no cubre esta reserva]`,
              idEstadoReserva: 5, // CANCELLED
              fueraHorarioMin: !!reserva.fueraHorarioMin
            };

            // Solo incluir idTipoAprobacion si existe
            const idTipoAprobacion = reserva.idTipoAprobacion || reserva.IdTipoAprobacion;
            if (idTipoAprobacion) {
              payloadReserva.idTipoAprobacion = idTipoAprobacion;
            }

            return axios.put(ENDPOINTS.reserva, payloadReserva);
          });

          await Promise.all(cancelPromises);
          console.log(`✅ Se cancelaron ${reservasACancelar.length} reserva(s) afectadas por la actualización.`);
        }

        // Actualizar la excepción
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);

        // Mostrar mensaje si hubo cancelaciones
        if (reservasACancelar.length > 0) {
          alert(`Excepción actualizada exitosamente.\n${reservasACancelar.length} reserva(s) fueron canceladas automáticamente.`);
        }
      }

      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar: ' + (e.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por chofer, nombre o motivo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Chofer {/* 👈 antes decía ID Chofer */}</th>
                  <th>Nombre excepción</th>
                  <th>Descripción</th>
                  <th>Fecha</th>
                  <th>Hora inicio</th>
                  <th>Hora fin</th>
                  <th>Motivo</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idExcepcion}>
                    <td>{normalizeValue(r.choferNombre)}</td>
                    <td><strong>{normalizeValue(r.nombreExcepcion)}</strong></td>
                    <td>{normalizeValue(r.descripcion)}</td>
                    <td>{normalizeValue(r.fechaInicio)}</td>
                    <td>{normalizeValue(r.horaInicio)}</td>
                    <td>{normalizeValue(r.horaFin)}</td>
                    <td>{normalizeValue(r.motivo)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idExcepcion)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          setEditingRowId(null);
                          askDelete(r.idExcepcion, r.nombreExcepcion || 'Excepción');
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={11}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
      />
    </div>
  );
}
